import { cx } from '../ui/cx.js';
import logoOnDark from './assets/logo-on-dark.webp';
import logoOnLight from './assets/logo-on-light.webp';

/**
 * Identité de la plateforme, direction « Rouge Pulse / Contraste » (05/10/2026, docs/DESIGN.md §6 bis).
 *
 * - `PlatformLogo` : logo raster du pack de marque (silhouette de toit et trois étapes, lettrage
 *   italique), en WebP réduit (24 Ko, ratio conservé, marges transparentes retirées). Version blanche
 *   sur le panneau anthracite, anthracite sur fond clair. Le **nom** annoncé aux lecteurs d'écran et le
 *   suffixe visible de la face garage (« Pro ») viennent de l'API (`public.platform`), jamais du code.
 * - `PlatformMark` : le pictogramme du favicon (toit rouge, ligne et trois étapes) en SVG, pour les
 *   tuiles carrées. Décoratif : le nom du produit porte le sens.
 */

/** Proportions du logo réduit (560 × 138). */
const LOGO_RATIO = 560 / 138;

export function PlatformLogo({
  name,
  suffix,
  tone,
  width = 188,
  className,
}: {
  /** Nom complet annoncé (face garage : nom Pro) ; `null` pendant le chargement. */
  name: string | null;
  /** Suffixe visible après le logo (ex. « Pro »), déduit des deux noms configurés. */
  suffix?: string;
  /** `dark` : sur le panneau anthracite (logo blanc) ; `light` : sur fond clair. */
  tone: 'dark' | 'light';
  width?: number;
  className?: string;
}) {
  return (
    <span
      role="img"
      aria-label={name ?? undefined}
      aria-hidden={name ? undefined : true}
      className={cx('inline-flex shrink-0 items-end gap-2', className)}
    >
      <img
        src={tone === 'dark' ? logoOnDark : logoOnLight}
        alt=""
        width={width}
        height={Math.round(width / LOGO_RATIO)}
        decoding="async"
        className="block h-auto select-none"
        draggable={false}
      />
      {suffix ? (
        <span
          aria-hidden
          className={cx(
            'mb-[2px] rounded-[5px] px-1.5 py-px text-[0.6875rem] leading-4 font-semibold tracking-[0.12em] uppercase',
            tone === 'dark' ? 'bg-platform-action text-white' : 'bg-platform-panel text-white',
          )}
        >
          {suffix}
        </span>
      ) : null}
    </span>
  );
}

export function PlatformMark({ size = 32, className }: { size?: number; className?: string }) {
  return (
    <svg aria-hidden focusable="false" width={size} height={size} viewBox="4 10 56 34" fill="none" className={cx('shrink-0', className)}>
      <path d="M7 28c12-1 12-11 27-11h7l8 8 8 2" className="stroke-platform-accent-on-panel" strokeWidth={4} strokeLinecap="round" />
      <path d="M8 38h48" stroke="currentColor" strokeWidth={3} />
      <g className="fill-platform-accent-on-panel">
        <circle cx={12} cy={38} r={5} />
        <circle cx={32} cy={38} r={5} />
        <circle cx={52} cy={38} r={5} />
      </g>
    </svg>
  );
}

/** Tuile anthracite du pictogramme (sur fond clair). */
export function PlatformMarkTile({ size = 40, className }: { size?: number; className?: string }) {
  return (
    <span
      aria-hidden
      className={cx('grid shrink-0 place-items-center rounded-xl bg-platform-panel text-on-platform', className)}
      style={{ width: size, height: size }}
    >
      <PlatformMark size={Math.round(size * 0.78)} />
    </span>
  );
}

/**
 * Repère graphique de la maquette : la ligne rouge verticale ponctuée de trois étapes (la ligne à points
 * du logo), posée sur le bord du panneau. Ambiance seulement (`aria-hidden`), sans image.
 */
export function PlatformTrack({ className }: { className?: string }) {
  return (
    <span aria-hidden className={cx('pointer-events-none absolute w-[3px] bg-platform-accent-on-panel', className)}>
      {['14%', '38%', '62%'].map((top) => (
        <i
          key={top}
          className="absolute left-1/2 size-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[5px] border-platform-accent-on-panel bg-white"
          style={{ top }}
        />
      ))}
    </span>
  );
}
